import React, { useContext, useMemo } from "react";
import { type FieldPath, type FieldValues } from "react-hook-form";
import { type GroupBase } from "react-select";
import {
  ReactSelectSyncProps,
  ReactSelectAsyncProps,
  ReactSelectController,
  ReactSelectControllerProps,
} from "@pushpress/shared-components";
import { AppContext } from "../../../context/app";
import {
  TaxSelectFragment,
  useGetTaxesQuery,
} from "./TaxSelect-generated.hooks";
import { sortByName } from "../../../utils/sortUtils";
import { Info } from "lucide-react";
import DisplayGif from "../../common/DisplayGif/DisplayGif";
import { Separator } from "@pushpress/pushpress-ui";
import { useLegacyNavigate } from "../../../hooks/useLegacyNavigate";

type TaxControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends TaxSelectFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ReactSelectControllerProps<TFieldValues, TName, Option, IsMulti, Group>;

function TaxSelectController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends TaxSelectFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: TaxControllerProps<TFieldValues, TName, Option, IsMulti, Group> &
    (ReactSelectSyncProps | ReactSelectAsyncProps),
) {
  const { ...other } = props;

  const app = useContext(AppContext);
  const legacyNavigate = useLegacyNavigate();

  const { data } = useGetTaxesQuery({
    variables: {
      getTaxes: {
        clientUuid: app.client.uuid,
      },
    },
  });

  const getOptionLabel = (option: TaxSelectFragment) => option.name;

  const getOptionValue = (option: TaxSelectFragment) => option.uuid;

  const options = useMemo(() => {
    if (!data) {
      return [];
    }
    return [...(data?.getTaxes || [])].sort(sortByName);
  }, [data]);

  const menuAdornment = (
    <>
      <Separator />
      <div className="tw-flex tw-items-center tw-gap-2 tw-p-2">
        <DisplayGif gif="taxSettings" size={500}>
          <Info className="tw-w-4 tw-h-4 tw-text-muted-foreground" />
        </DisplayGif>

        <span className="tw-text-sm tw-text-muted-foreground">
          Navigate to{" "}
          <span
            role="button"
            tabIndex={0}
            className="tw-text-link hover:tw-text-link-hover"
            onClick={() => {
              legacyNavigate("/settings/index");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                legacyNavigate("/settings/index");
              }
            }}
          >
            settings
          </span>{" "}
          to add your tax rate.
        </span>
      </div>
    </>
  );

  return (
    <ReactSelectController
      menuPortalTarget={document.body}
      {...other}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      menuAdornment={menuAdornment}
    />
  );
}

export default TaxSelectController;
